import { ReparoApi } from './index';

export type MaterialType = 'RM' | 'PM' | 'STAGE';

export type Material = {
    materialId: string;
    merchantId: string;
    sku?: string | null;
    name: string;
    category: string;
    materialType: MaterialType;
    cost?: number | null;
    unit: string;
    /** GST slab %, e.g. 18. `null` means not stated — distinct from 0%, a real slab. */
    gstRate?: number | null;
    hsnCode?: string | null;
    /** `false` retires the material; `null`/`true` is active. Hidden from pickers when false. */
    isActive?: boolean | null;
};

/**
 * Body for creating a material. There is no `materialId` field — the backend derives
 * it from `code`, so one value lands in both `material_id` and `sku`.
 */
export type CreateMaterialPayload = {
    code: string;
    name: string;
    category: string;
    materialType: MaterialType;
    cost?: number;
    unit?: string;
    gstRate?: number;
    hsnCode?: string;
};

/** ADMIN only. Rejects with 409 if the code is already taken. */
export const createMaterial = async (
    payload: CreateMaterialPayload,
): Promise<Material> => {
    return ReparoApi<any>({
        method: 'POST',
        url: '/materials',
        body: JSON.stringify(payload),
    });
};

/** Merchant-scoped: returns only the authenticated user's materials. */
export const getMaterials = async (): Promise<Material[]> => {
    return ReparoApi<any>({
        method: 'GET',
        url: '/materials',
    });
};

export type BulkCreateMaterialsResult = {
    totalRows: number;
    /** Rows created — or, on a dry run, rows that *would* be created. */
    created: number;
    /** Rows whose code already exists. Create-only: these are never updated. */
    skipped: number;
    /** Rows that existed and were changed. Only the upsert importer sets this. */
    updated?: number;
    errors: Array<{ row: number; code: string; reason: string }>;
    dryRun: boolean;
};

/** CSV columns the importer understands, in template order. */
export const MATERIAL_CSV_COLUMNS = [
    'code',
    'name',
    'category',
    'material_type',
    'cost',
    'unit',
    'gst_rate',
    'hsn_code',
] as const;

/** Of those, the ones a row cannot omit. `material_type` is never defaulted. */
export const MATERIAL_CSV_REQUIRED_COLUMNS = [
    'code',
    'name',
    'category',
    'material_type',
] as const;

/**
 * ADMIN only. With `dryRun` the backend validates and counts without writing —
 * used to preview an import before committing it.
 */
export const bulkCreateMaterials = async (
    file: File,
    dryRun: boolean,
): Promise<BulkCreateMaterialsResult> => {
    const formData = new FormData();
    formData.append('file', file);
    return ReparoApi<any>({
        method: 'POST',
        url: `/materials/bulk-create?dryRun=${dryRun}`,
        body: formData,
    });
};

/**
 * Units we suggest. Deliberately *not* a whitelist — brands measure in their own
 * vocabulary (Healthfab counts thread in `Cones`), so the picker allows free entry
 * and the merchant's own existing units are merged in ahead of these.
 */
/**
 * Columns the upsert importer understands — the download's own header line.
 *
 * Led by `material_id` because that is what an edited row is matched on, and carrying `sku`
 * as its own column rather than folding it into `code`: most merchants hold no sku at all,
 * and Nike's do not match their ids, so the create importer's one-column shortcut cannot
 * describe them.
 */
export const MATERIAL_UPSERT_CSV_COLUMNS = [
    'material_id',
    'name',
    'sku',
    'category',
    'material_type',
    'cost',
    'unit',
    'gst_rate',
    'hsn_code',
    'is_active',
    'brand',
] as const;

/**
 * Only the key. Everything else is optional because a blank cell means "leave this alone" —
 * though a row whose id is NEW must also carry name, category and material_type, which the
 * importer reports per row rather than refusing the whole file for.
 */
export const MATERIAL_UPSERT_REQUIRED_COLUMNS = ['material_id'] as const;

/**
 * ADMIN only. Updates rows whose `material_id` exists and creates the ones that do not.
 * With `dryRun` the backend runs every check and writes nothing.
 */
export const bulkUpsertMaterials = async (
    file: File,
    dryRun: boolean,
): Promise<BulkCreateMaterialsResult> => {
    const formData = new FormData();
    formData.append('file', file);
    return ReparoApi<any>({
        method: 'POST',
        url: `/materials/bulk-upsert?dryRun=${dryRun}`,
        body: formData,
    });
};

export const SUGGESTED_MATERIAL_UNITS = [
    'pcs',
    'kg',
    'g',
    'm',
    'cm',
    'mL',
    'L',
    'sqm',
    'sqft',
    'pair',
] as const;

export const DEFAULT_MATERIAL_UNIT = 'pcs';

/**
 * The units to offer: the merchant's own ahead of the suggested ones, de-duplicated
 * case-insensitively, otherwise a merchant holding both `kg` and `Kg` would be offered the
 * same unit twice, which is exactly the drift the backend folds away.
 */
export const buildUnitOptions = (merchantUnits: string[]): string[] => {
    const seen = new Set<string>();
    const options: string[] = [];

    for (const unit of [...merchantUnits, ...SUGGESTED_MATERIAL_UNITS]) {
        const key = unit.trim().toLowerCase();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        options.push(unit);
    }

    return options;
};
